import { z } from '@hono/zod-openapi';

import { InstantSchema, LocalDateSchema, Sha256Schema, UUIDSchema } from './common';

export const EntityMetadataSchema = z
  .object({
    id: UUIDSchema,
    createdAt: InstantSchema,
    updatedAt: InstantSchema,
    revision: z.number().int().positive(),
    deletedAt: InstantSchema.optional(),
  })
  .strict();

export const RegistrationWindowSchema = z
  .object({ startsAt: InstantSchema, endsAt: InstantSchema, label: z.string().min(1).max(100) })
  .strict();

export const ExamSchema = EntityMetadataSchema.extend({
  title: z.string().trim().min(1).max(120),
  presetId: z.string().min(1).max(120).optional(),
  examDate: LocalDateSchema.nullish(),
  timezone: z.string().min(1).max(100),
  registrationWindows: z.array(RegistrationWindowSchema).max(20).optional(),
  result: z.enum(['passed', 'failed', 'unknown']).optional(),
  stage: z.string().max(80).optional(),
  retainedArchive: z.boolean().optional(),
  attempts: z
    .array(
      z
        .object({ examDate: LocalDateSchema, result: z.enum(['passed', 'failed', 'unknown']) })
        .strict(),
    )
    .optional(),
}).openapi('Exam');

export const SubjectSchema = EntityMetadataSchema.extend({
  examId: UUIDSchema,
  name: z.string().trim().min(1).max(100),
  totalUnits: z.number().int().positive().max(1_000_000).nullable(),
  unitLabel: z.enum(['page', 'lecture', 'question', 'custom']),
  customUnitLabel: z.string().trim().min(1).max(20).optional(),
  sortOrder: z.number().int().nonnegative(),
})
  .superRefine((value, context) => {
    if (value.unitLabel === 'custom' && !value.customUnitLabel) {
      context.addIssue({
        code: 'custom',
        path: ['customUnitLabel'],
        message: 'Required for custom unit',
      });
    }
  })
  .openapi('Subject');

export const StudyLogSchema = EntityMetadataSchema.extend({
  examId: UUIDSchema,
  subjectId: UUIDSchema,
  studyDate: LocalDateSchema,
  startedAt: InstantSchema.optional(),
  endedAt: InstantSchema.optional(),
  minutes: z.number().int().min(1).max(1_440).optional(),
  round: z.number().int().positive().max(100),
  unitStart: z.number().int().positive(),
  unitEnd: z.number().int().positive(),
  memo: z.string().max(10_000).optional(),
  source: z.enum(['manual', 'room_prefill', 'onboarding_import', 'plan']),
  planItemId: UUIDSchema.optional(),
})
  .superRefine((value, context) => {
    if (value.unitEnd < value.unitStart) {
      context.addIssue({ code: 'custom', path: ['unitEnd'], message: 'Must be >= unitStart' });
    }
  })
  .openapi('StudyLog');

export const WrongNoteSchema = EntityMetadataSchema.extend({
  examId: UUIDSchema,
  subjectId: UUIDSchema,
  photoAssetId: UUIDSchema.optional(),
  text: z.string().max(50_000).optional(),
  memo: z.string().max(20_000).optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(30),
  reviewCount: z.number().int().nonnegative(),
  lastRecall: z.enum(['remembered', 'uncertain', 'forgotten']).optional(),
  relearningAt: InstantSchema.optional(),
  lastReviewedAt: InstantSchema.optional(),
  masteredAt: InstantSchema.optional(),
})
  .superRefine((value, context) => {
    if (!value.text?.trim() && !value.photoAssetId) {
      context.addIssue({ code: 'custom', path: ['text'], message: 'Text or photo is required' });
    }
  })
  .openapi('WrongNote');

export const PhotoAssetManifestSchema = z
  .object({
    id: UUIDSchema,
    originalPath: z.string().min(1),
    thumbnailPath: z.string().min(1),
    mimeType: z.string().min(1).max(100),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    byteLength: z.number().int().positive(),
    sha256: Sha256Schema,
    createdAt: InstantSchema,
    deletedAt: InstantSchema.optional(),
  })
  .strict()
  .openapi('PhotoAssetManifest');

export const PlanParamsSchema = z
  .object({
    timezone: z.string().min(1),
    targetRounds: z.number().int().positive(),
    studyWeekdays: z.array(z.number().int().min(0).max(6)).min(1).max(7),
    excludedDates: z.array(LocalDateSchema),
    maxDailyUnits: z.number().int().positive().optional(),
    subjectWeights: z.record(UUIDSchema, z.number().positive()),
  })
  .strict();

export const PlanSchema = z
  .object({
    id: UUIDSchema,
    examId: UUIDSchema,
    version: z.number().int().positive(),
    status: z.enum(['draft', 'active', 'superseded']),
    params: PlanParamsSchema,
    generatedAt: InstantSchema,
    confirmedAt: InstantSchema.optional(),
    supersedesId: UUIDSchema.optional(),
  })
  .strict()
  .openapi('Plan');

export const PlanItemSchema = z
  .object({
    id: UUIDSchema,
    planId: UUIDSchema,
    date: LocalDateSchema,
    subjectId: UUIDSchema,
    round: z.number().int().positive(),
    unitStart: z.number().int().positive(),
    unitEnd: z.number().int().positive(),
    status: z.enum(['planned', 'done', 'skipped']),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.unitEnd < value.unitStart) {
      context.addIssue({ code: 'custom', path: ['unitEnd'], message: 'Must be >= unitStart' });
    }
  })
  .openapi('PlanItem');

export const MilestoneSchema = z
  .object({
    id: UUIDSchema,
    key: z.string().min(1).max(100),
    examId: UUIDSchema.optional(),
    earnedAt: InstantSchema,
    sourceEntityId: UUIDSchema.optional(),
  })
  .strict()
  .openapi('Milestone');

export const MogakSessionSchema = z
  .object({
    id: UUIDSchema,
    roomId: UUIDSchema,
    examId: UUIDSchema,
    joinedAt: InstantSchema,
    leftAt: InstantSchema.optional(),
    activeMinutes: z.number().int().nonnegative().max(1_440),
    outcome: z.enum(['prefill_saved', 'discarded', 'disconnected', 'kicked']),
    savedLogId: UUIDSchema.optional(),
  })
  .strict()
  .openapi('MogakSession');

export const MockScoreSchema = EntityMetadataSchema.extend({
  examId: UUIDSchema,
  title: z.string().trim().min(1).max(120),
  studyDate: LocalDateSchema,
  scores: z
    .array(z.object({ subjectId: UUIDSchema, score: z.number().min(0).max(100) }).strict())
    .min(1)
    .max(20),
});
export const NoteInboxSchema = EntityMetadataSchema.extend({ photoAssetId: UUIDSchema });
export const PortableSettingSchema = z
  .object({
    key: z.string().regex(/^(weekly-goal|exam-checklist|pass-criteria):[a-zA-Z0-9-]+$/),
    value: z.unknown(),
    updatedAt: InstantSchema,
  })
  .strict();
